import classNames from 'classnames';
import { FormikErrors, FormikTouched, useFormik } from 'formik';
import Input from '../../components/Input/Input';
import { isEmail } from '../../core/helpers';
import { ChangeEventHandler, useState } from 'react';
import { Loader } from '../../components/Loader/Loader';
import { useTranslation } from 'react-i18next';
import Header from '../../components/Header/Header';
import { Link } from 'react-router-dom';
import { ReactComponent as EmailIcon } from '../../icons/forgot-password-email.svg';
import { forgotPassword } from '../../core/auth';
import Button from '../../components/Button';

interface FormValues {
  email: string;
}

const ForgotPassword = () => {
  const { t } = useTranslation();

  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSent, setIsSent] = useState(false);

  const formik = useFormik({
    validateOnBlur: true,
    initialValues: {
      email: '',
    },
    validate: (values) => {
      const errors: FormikErrors<FormValues> = {};
      if (!values.email) {
        errors.email = t('Required');
      }
      if (values.email !== '' && !isEmail(values.email)) {
        errors.email = t(`Email is not valid`);
      }

      return errors;
    },
    onSubmit: ({ email }) => {
      if (error) setError('');
      setIsLoading(true);
      forgotPassword({ email })
        .then(() => {
          setIsSent(true);
        })
        .catch(console.error)
        .finally(() => {
          setIsLoading(false);
        });
    },
  });

  const onChangeField: ChangeEventHandler<HTMLInputElement> = (e) => {
    if (error) setError('');
    formik.setFieldValue(
      e.target.name,
      e.target.value,
      formik.touched[e.target.name as keyof FormikTouched<FormValues>]
    );
  };

  if (isLoading) return <Loader />;

  return (
    <>
      <Header />
      <div
        className='Page Page--Login d-flex align-items-center justify-content-center'
        data-testid='forgot-password'
      >
        <div className='Login__container mb-lg-5 pb-lg-5'>
          {isSent ? (
            <div className='text-center'>
              <EmailIcon />
              <div className='Title--lg text-lg-center mb-3 fw-900 mt-4 pt-lg-2'>
                {t('Check your email')}
              </div>
              <div className='Text mb-3'>
                {t(
                  'We have emailed you at *email* the link and verification code to reset your password'
                ).replace('*email*', formik.values.email)}
              </div>
              <div className='text-center Text'>
                <Link to='/' className='Link--blue'>
                  {t('Return to login')}
                </Link>
              </div>
            </div>
          ) : (
            <>
              <div className='Title--lg text-lg-center mb-3 fw-900'>
                {t('Reset your password')}
              </div>
              <div
                className='Text text-center mb-4 mx-auto'
                style={{
                  maxWidth: 400,
                }}
              >
                {t(
                  'We’ll email you instructions to reset your password. If you still have problems with logging in, contact your manager.'
                )}
              </div>
              <form onSubmit={formik.handleSubmit} noValidate className='w-100'>
                <div className={classNames('Login__content align-items-start')}>
                  <Input
                    label='Email'
                    placeholder='Enter your email'
                    name='email'
                    id='email'
                    onChange={onChangeField}
                    onBlur={formik.handleBlur}
                    value={formik.values.email}
                    error={formik.errors.email}
                    touched={formik.touched.email}
                  />
                </div>
                <div>
                  <Button
                    className='Login__btn mt-3 w-100'
                    type='submit'
                    disabled={isLoading}
                  >
                    {t('Reset password')}
                  </Button>
                  {error && (
                    <div className='Login__error text-center'>{t(error)}</div>
                  )}
                </div>
              </form>
              <div className='text-center mt-4 pt-lg-2 Text'>
                <Link to='/' className='Link--blue'>
                  {t('Return to login')}
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default ForgotPassword;
